'use client'

import {
  AutoAwesome,
  BookmarkAddOutlined,
  ChevronRight,
  Close,
  CloseFullscreen,
  CropFree,
  ExpandMore,
  FastForward,
  History,
  LinkOutlined,
  NotificationsNone,
  OpenInFull,
  PanToolOutlined,
  PlayArrowOutlined,
  Psychology,
  Send,
  Stop,
  SupportOutlined,
  UploadFileOutlined
} from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme
} from '@mui/material'
import { useFormatter, useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSettings } from '@core/context/settingsContext'

import Link from '~/components/Link'
import { useGateValue } from '~/configs/statsig'
import { useLazyGetConversationQuery, useListConversationsQuery, useShareConversationMutation } from '~/data/api/client'
import type { AssistantSkill } from '~/data/api/client'
import { logger } from '~/debug/logger'
import { BillingDraftMessageBlock } from '~/features/billing-composer'
import type { BillingDraft } from '~/features/billing-composer/types'
import routeDirectory from '~/navigation/routes'
import { useCurrentOrgId } from '~/store/slices/currentOrg'
import { useBannerOffset, useSnackbarControls } from '~/store/slices/display'
import { useMediaQuery } from '~/theme/hooks'

import AssistantEmptySuggestions from './AssistantEmptySuggestions'
import AssistantHelpPanel from './AssistantHelpPanel'
import AssistantMarkdown from './AssistantMarkdown'
import { useAssistantRunSkill } from './AssistantRunSkillContext'
import { AttachFileButton, MessageAttachmentChips, PendingAttachmentChips } from './AttachmentChips'
import {
  AssistantActivity,
  AssistantMessageStack,
  type AssistantActivityItem,
  type AssistantActivityLabels
} from './ChatBubbles'
import ComposerContextTag from './ComposerContextTag'
import SaveSkillFromConversationDialog from './SaveSkillFromConversationDialog'
import mapPersistedMessages from './conversationHistory'
import { type FocusedContextRef } from './contextRef'
import assistantCategoryIcons from './mentionIcons'
import { mergeSkillContextRefs } from './skillUtils'
import type { AssistantMessage, AssistantStreamEvent } from './streamTypes'
import useAssistantAttachments from './useAssistantAttachments'
import useAssistantMentions, { type AssistantMentionCandidate } from './useAssistantMentions'
import useAssistantSkills from './useAssistantSkills'
import useAssistantStream from './useAssistantStream'
import useFileDropZone from './useFileDropZone'
import useFocusPageContext from './useFocusPageContext'
import useOpsNotifications from './useOpsNotifications'

const NAV_WIDTH = 260
const COLLAPSED_NAV_WIDTH = 68
const ASSISTANT_BAR_HEIGHT = 49

/**
 * CSS custom property name that AssistantBar sets on <html> while it is
 * mounted. Any element that needs to render above the bar can use
 * `bottom: var(--synop-assistant-bar-height, 0px)` without importing this
 * module.
 */
export const ASSISTANT_BAR_HEIGHT_VAR = '--synop-assistant-bar-height'

export default function AssistantBar() {
  const t = useTranslations('features.AssistantBar')
  const tAttachments = useTranslations('features.AssistantAttachments')
  const format = useFormatter()
  const theme = useTheme()
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const orgId = useCurrentOrgId()
  const { settings } = useSettings()
  const bannerOffset = useBannerOffset()
  const navHidden = useMediaQuery(theme.breakpoints.down('lg'))
  const focusPageRef = useFocusPageContext(pathname)
  const billingComposerEnabled = useGateValue('billing-composer')
  const {
    sendMessage,
    ensureConversation,
    runAction,
    streamActionProgress,
    abort,
    resetConversation,
    resumeConversation,
    getActiveConversationId
  } = useAssistantStream(orgId)
  const attachments = useAssistantAttachments({ organizationId: orgId, ensureConversation })
  const { addFiles, clear: clearAttachments, waitForUploads, takeReady } = attachments
  const opsNotifications = useOpsNotifications(Boolean(orgId))
  const [shareConversation] = useShareConversationMutation()
  const { pendingRun, runSkill, acknowledgePendingRun } = useAssistantRunSkill()

  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [draft, setDraft] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [pendingActionId, setPendingActionId] = useState<string | null>(null)
  const [pendingActionReason, setPendingActionReason] = useState<string | null>(null)
  const [pendingActionPreview, setPendingActionPreview] = useState<string | null>(null)
  const [, setPendingActionType] = useState<string | null>(null)
  const [pendingActionLongRunning, setPendingActionLongRunning] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [contextRefs, setContextRefs] = useState<FocusedContextRef[]>([])
  const [actMode, setActMode] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null)
  const [isSkillPickerOpen, setIsSkillPickerOpen] = useState(false)
  const [saveSkillConversationId, setSaveSkillConversationId] = useState<string | null>(null)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [conversationReadOnly, setConversationReadOnly] = useState(false)
  const [conversationShareScope, setConversationShareScope] = useState<string>('personal')
  const [isSharingLink, setIsSharingLink] = useState(false)

  const { openSnackbar } = useSnackbarControls()
  const { isDragging, dropZoneProps } = useFileDropZone({ onFiles: addFiles, disabled: !orgId || conversationReadOnly })
  const skills = useAssistantSkills(orgId, isOpen)
  const activityLabels = useMemo<AssistantActivityLabels>(
    () => ({
      activity: t('activity'),
      collapse: t('activityCollapse'),
      completed: t('activityCompleted'),
      done: t('activityDone'),
      emptyDetails: t('activityEmptyDetails'),
      expand: t('activityExpand'),
      reasoning: t('activityReasoning'),
      running: t('activityRunning'),
      skillCount: (count) => t('activitySkillCount', { count }),
      thinking: t('thinking'),
      toolCount: (count) => t('activityToolCount', { count }),
      working: t('activityWorking')
    }),
    [t]
  )

  // Conversation list (history panel) and on-demand detail fetch via the generated
  // RTK Query hooks. The list is fetched declaratively while the panel is open;
  // opening a conversation triggers the lazy detail query.
  const {
    data: historyData,
    isFetching: isHistoryLoading,
    isError: historyError,
    refetch: refetchHistory
  } = useListConversationsQuery(
    { 'X-Organization-Id': orgId ?? '', limit: 30 },
    { skip: !isHistoryOpen || !orgId, refetchOnMountOrArgChange: true }
  )
  const historyItems = historyData?.conversations ?? []
  const [fetchConversation] = useLazyGetConversationQuery()

  const inputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const lastOrgIdRef = useRef<string | undefined>(orgId)
  const [barHeight, setBarHeight] = useState(ASSISTANT_BAR_HEIGHT)

  useEffect(() => {
    return () => {
      abort()
    }
  }, [abort])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const size = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
      setBarHeight(size)
      document.documentElement.style.setProperty(ASSISTANT_BAR_HEIGHT_VAR, `${size}px`)
    })
    observer.observe(root)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty(ASSISTANT_BAR_HEIGHT_VAR)
    }
  }, [])

  useEffect(() => {
    if (lastOrgIdRef.current !== orgId) {
      lastOrgIdRef.current = orgId
      setMessages([])
      setContextRefs([])
      setPendingActionId(null)
      setPendingActionReason(null)
      setPendingActionPreview(null)
      setPendingActionType(null)
      setPendingActionLongRunning(false)
      setActionDispatched(false)
      setActionDispatchedReason(null)
      setDraft('')
      setIsStreaming(false)
      setIsSkillPickerOpen(false)
      acknowledgePendingRun()
    }
  }, [acknowledgePendingRun, orgId])

  useEffect(() => {
    if (!isOpen) return
    const lastMessage = messages.at(-1)
    const isCurrentlyStreaming = lastMessage?.role === 'assistant' && lastMessage.streaming
    messagesEndRef.current?.scrollIntoView({ behavior: isCurrentlyStreaming ? 'instant' : 'smooth' })
  }, [messages, isOpen])

  useEffect(() => {
    setContextRefs((previous) =>
      previous.map((item) => {
        if (item.type === focusPageRef.type && item.id === focusPageRef.id && item.label !== focusPageRef.label) {
          return { ...item, label: focusPageRef.label }
        }
        return item
      })
    )
  }, [focusPageRef])

  const mentionQuery = useMemo(() => {
    const index = draft.lastIndexOf('@')
    if (index < 0) return null
    return draft
      .slice(index + 1)
      .trim()
      .toLowerCase()
  }, [draft])

  const mentionCandidates = useAssistantMentions(mentionQuery, { enabled: isOpen && !conversationReadOnly })

  const applySkillToState = useCallback((skill: AssistantSkill) => {
    if (skill.defaultAutoApprove != null) {
      setActMode(skill.defaultAutoApprove)
    }
    setContextRefs((previous) => mergeSkillContextRefs(previous, skill))
  }, [])

  const executeTurn = useCallback(
    async ({
      message,
      skillId,
      contextRefs: contextRefsOverride,
      autoApprove: autoApproveOverride
    }: {
      message: string
      skillId?: string
      contextRefs?: FocusedContextRef[]
      autoApprove?: boolean
    }) => {
      if (!orgId || isStreaming) return

      await waitForUploads()
      const sentAttachments = takeReady()
      const trimmed = message.trim() || (sentAttachments.length > 0 ? tAttachments('attach_defaultMessage') : '')
      const userMessage: AssistantMessage | null = trimmed
        ? {
            id: crypto.randomUUID(),
            role: 'user',
            content: trimmed,
            attachments: sentAttachments.length > 0 ? sentAttachments : undefined
          }
        : null
      const assistantMessage: AssistantMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        thinking: '',
        streaming: true,
        toolCalls: []
      }
      setMessages((previous) => [...previous, ...(userMessage ? [userMessage] : []), assistantMessage])
      setPendingActionId(null)
      setPendingActionReason(null)
      setPendingActionPreview(null)
      setIsStreaming(true)
      setIsSkillPickerOpen(false)

      try {
        await sendMessage({
          organizationId: orgId,
          message: trimmed,
          contextRefs: contextRefsOverride ?? contextRefs,
          pageContext: { route: pathname, pageTitle: focusPageRef.label ?? pathname },
          autoApprove: autoApproveOverride ?? actMode,
          skillId,
          attachmentIds: sentAttachments.map((attachment) => attachment.attachmentId),
          onEvent: (event) => {
            applyStreamEvent(
              event,
              setMessages,
              setPendingActionId,
              setPendingActionReason,
              setPendingActionType,
              setPendingActionLongRunning,
              setPendingActionPreview
            )
            if (event.type === 'done' || event.type === 'error' || event.type === 'cancelled') {
              setIsStreaming(false)
            }
          }
        })
        const conversationId = getActiveConversationId()
        if (conversationId) {
          setActiveConversationId(conversationId)
          setConversationReadOnly(false)
          setConversationShareScope('personal')
        }
      } finally {
        setIsStreaming(false)
      }
    },
    [
      actMode,
      contextRefs,
      focusPageRef.label,
      getActiveConversationId,
      isStreaming,
      orgId,
      pathname,
      sendMessage,
      tAttachments,
      takeReady,
      waitForUploads
    ]
  )

  const canSubmit =
    (Boolean(draft.trim()) || attachments.readyCount > 0) &&
    !attachments.isUploading &&
    Boolean(orgId) &&
    !isStreaming &&
    !conversationReadOnly

  const submit = useCallback(async () => {
    if (!canSubmit) return
    const messageToSend = draft.trim()
    setDraft('')
    await executeTurn({ message: messageToSend })
  }, [canSubmit, draft, executeTurn])

  useEffect(() => {
    if (!pendingRun || !orgId || isStreaming) return

    const { skill, message = '', newChat = true } = pendingRun
    acknowledgePendingRun()

    setIsHistoryOpen(false)
    setIsHelpOpen(false)
    setIsOpen(true)

    const baseRefs = newChat ? [] : contextRefs
    const mergedRefs = mergeSkillContextRefs(baseRefs, skill)
    const autoApprove = skill.defaultAutoApprove ?? actMode

    if (newChat) {
      resetConversation()
      clearAttachments()
      setMessages([])
      setPendingActionId(null)
      setPendingActionReason(null)
      setPendingActionPreview(null)
      setPendingActionType(null)
      setPendingActionLongRunning(false)
      setActionDispatched(false)
      setActionDispatchedReason(null)
      setDraft('')
    }

    applySkillToState(skill)
    setContextRefs(mergedRefs)
    if (skill.defaultAutoApprove != null) {
      setActMode(skill.defaultAutoApprove)
    }

    void executeTurn({
      message,
      skillId: skill.id,
      contextRefs: mergedRefs,
      autoApprove
    })
  }, [
    acknowledgePendingRun,
    actMode,
    applySkillToState,
    clearAttachments,
    contextRefs,
    executeTurn,
    isStreaming,
    orgId,
    pendingRun,
    resetConversation
  ])

  const addCurrentPageContext = useCallback(() => {
    setContextRefs((previous) => {
      const withoutDuplicate = previous.filter(
        (item) => !(item.type === focusPageRef.type && item.id === focusPageRef.id)
      )
      return [...withoutDuplicate, focusPageRef]
    })
  }, [focusPageRef])

  const removeContext = useCallback((ref: FocusedContextRef) => {
    setContextRefs((previous) => previous.filter((item) => !(item.type === ref.type && item.id === ref.id)))
  }, [])

  const addMentionContext = useCallback((item: AssistantMentionCandidate) => {
    setContextRefs((previous) => {
      const exists = previous.some((contextRef) => contextRef.type === item.category && contextRef.id === item.id)
      if (exists) return previous
      return [...previous, item.contextRef]
    })
    setDraft((previous) => {
      const atIndex = previous.lastIndexOf('@')
      if (atIndex < 0) return previous
      return previous.slice(0, atIndex)
    })
    inputRef.current?.focus()
  }, [])

  /** Puts a suggested request into the input without sending it, so the user can edit before Enter. */
  const seedInput = useCallback((text: string) => {
    setDraft(text)
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [])

  const [actionDispatched, setActionDispatched] = useState(false)
  const [actionDispatchedReason, setActionDispatchedReason] = useState<string | null>(null)

  const runProposedAction = useCallback(async () => {
    if (!pendingActionId || isStreaming) return
    const actionId = pendingActionId
    const isLongRunning = pendingActionLongRunning
    const reason = pendingActionReason
    setPendingActionId(null)
    setPendingActionReason(null)
    setPendingActionPreview(null)
    setPendingActionType(null)
    setPendingActionLongRunning(false)
    setActionDispatched(true)
    setActionDispatchedReason(reason)
    setMessages((previous) => [
      ...previous,
      { id: crypto.randomUUID(), role: 'assistant', content: '', thinking: '', streaming: true, toolCalls: [] }
    ])
    setIsStreaming(true)

    const progressMessageId = crypto.randomUUID()
    if (isLongRunning) {
      setMessages((previous) => [
        ...previous,
        {
          id: progressMessageId,
          role: 'assistant',
          content: '',
          thinking: '',
          streaming: true,
          toolCalls: [],
          isActionProgress: true,
          phase: 'starting',
          phaseMessage: reason ?? t('actionProgressStarting')
        }
      ])
      void streamActionProgress(actionId, (event) => {
        applyActionStreamEvent(event, progressMessageId, setMessages)
      })
    }

    try {
      await runAction(actionId, (event) => {
        applyStreamEvent(
          event,
          setMessages,
          setPendingActionId,
          setPendingActionReason,
          setPendingActionType,
          setPendingActionLongRunning,
          setPendingActionPreview
        )
        if (event.type === 'action_stream' && event.actionId && event.longRunning && !isLongRunning) {
          void streamActionProgress(event.actionId, (progressEvent) => {
            applyActionStreamEvent(progressEvent, progressMessageId, setMessages)
          })
        }
        if (event.type === 'done' || event.type === 'error' || event.type === 'cancelled') {
          setIsStreaming(false)
          setActionDispatched(false)
          setActionDispatchedReason(null)
        }
      })
    } finally {
      setIsStreaming(false)
    }
  }, [isStreaming, pendingActionId, pendingActionReason, pendingActionLongRunning, runAction, streamActionProgress, t])

  const stopStreaming = useCallback(() => {
    abort()
  }, [abort])

  const startNewChat = useCallback(() => {
    resetConversation()
    clearAttachments()
    setActiveConversationId(null)
    setConversationReadOnly(false)
    setConversationShareScope('personal')
    setMessages([])
    setContextRefs([])
    setPendingActionId(null)
    setPendingActionReason(null)
    setPendingActionPreview(null)
    setPendingActionType(null)
    setPendingActionLongRunning(false)
    setActionDispatched(false)
    setActionDispatchedReason(null)
    setDraft('')
    setIsStreaming(false)
    setIsSkillPickerOpen(false)
    inputRef.current?.focus()
  }, [clearAttachments, resetConversation])

  const runSkillFromPicker = useCallback(
    (skill: AssistantSkill) => {
      setIsSkillPickerOpen(false)
      runSkill(skill, { newChat: messages.length === 0, message: '' })
    },
    [messages.length, runSkill]
  )

  const formatTimestamp = useCallback(
    (iso?: string) => {
      if (!iso) return ''
      const date = new Date(iso)
      if (Number.isNaN(date.getTime())) return ''
      return format.relativeTime(date)
    },
    [format]
  )

  const openConversation = useCallback(
    async (conversationId: string) => {
      if (!orgId || loadingConversationId) return
      setLoadingConversationId(conversationId)
      try {
        const detail = await fetchConversation({ conversationId, 'X-Organization-Id': orgId }).unwrap()
        abort()
        clearAttachments()
        setMessages(mapPersistedMessages(detail.messages ?? []))
        setContextRefs([])
        setActiveConversationId(conversationId)
        setConversationReadOnly(Boolean(detail.readOnly))
        setConversationShareScope(detail.conversation?.shareScope ?? 'personal')

        // Backend stores status as lowercase "proposed"; normalize for restore.
        const pendingAction = detail.readOnly
          ? undefined
          : detail.actions?.find((action) => action.status?.toUpperCase() === 'PROPOSED')
        if (pendingAction?.id) {
          setPendingActionId(pendingAction.id)
          setPendingActionReason(pendingAction.outputSummary ?? 'The assistant proposed an action.')
          setPendingActionType(pendingAction.type ?? null)
          setPendingActionLongRunning(Boolean(pendingAction.longRunning))
        } else {
          setPendingActionId(null)
          setPendingActionReason(null)
          setPendingActionPreview(null)
          setPendingActionType(null)
          setPendingActionLongRunning(false)
        }

        setActionDispatched(false)
        setActionDispatchedReason(null)
        setDraft('')
        setIsStreaming(false)
        resumeConversation(conversationId)
        setIsHistoryOpen(false)
        setIsHelpOpen(false)
        setIsOpen(true)
        setTimeout(() => inputRef.current?.focus(), 60)
      } catch (error: unknown) {
        if (typeof error === 'object' && error !== null && 'status' in error) {
          const status = (error as { status?: number }).status
          if (status === 403) {
            openSnackbar(t('shareForbidden'), { severity: 'error' })
          } else if (status === 404) {
            openSnackbar(t('shareNotFound'), { severity: 'error' })
          }
        }
        logger.error('Failed to open assistant conversation', error)
      } finally {
        setLoadingConversationId(null)
      }
    },
    [abort, clearAttachments, fetchConversation, loadingConversationId, openSnackbar, orgId, resumeConversation, t]
  )

  const copyShareLink = useCallback(async () => {
    if (!activeConversationId || !orgId || conversationReadOnly || isSharingLink) return
    setIsSharingLink(true)
    try {
      let shareScope = conversationShareScope
      if (shareScope !== 'organization') {
        const updated = await shareConversation({
          conversationId: activeConversationId,
          'X-Organization-Id': orgId,
          assistantConversationShareParams: { shareScope: 'organization' }
        }).unwrap()
        shareScope = updated.shareScope ?? 'organization'
        setConversationShareScope(shareScope)
      }
      // Share the read-only conversation page rather than a deep link into the current page's chat bar.
      const url = new URL(routeDirectory.assistant.conversation(activeConversationId), window.location.origin)
      await navigator.clipboard.writeText(url.toString())
      openSnackbar(t('shareLinkCopied'), { severity: 'success' })
    } catch (error: unknown) {
      logger.error('Failed to copy assistant share link', error)
      openSnackbar(t('shareLinkError'), { severity: 'error' })
    } finally {
      setIsSharingLink(false)
    }
  }, [
    activeConversationId,
    conversationReadOnly,
    conversationShareScope,
    isSharingLink,
    openSnackbar,
    orgId,
    shareConversation,
    t
  ])

  const openOpsNotification = useCallback(
    async (notification: {
      id?: string
      conversationId?: string
      entityId?: string
      entityType?: string
      type?: string
    }) => {
      if (notification.id) {
        await opsNotifications.markRead(notification.id)
      }
      setIsNotificationsOpen(false)

      const isFirmwareRollout =
        notification.entityType === 'firmware_deployment' ||
        notification.type === 'FIRMWARE_ROLLOUT_COMPLETE' ||
        notification.type === 'FIRMWARE_ROLLOUT_NEEDS_ATTENTION'
      if (isFirmwareRollout && notification.entityId) {
        setIsOpen(false)
        setIsExpanded(false)
        router.push(routeDirectory.chargers.rollouts.details(notification.entityId))
        return
      }
      if (notification.conversationId) {
        await openConversation(notification.conversationId)
      }
    },
    [openConversation, opsNotifications, router]
  )

  useEffect(() => {
    const conversationId = searchParams.get('assistantConversation')
    if (!conversationId || !orgId) {
      return
    }
    void openConversation(conversationId)
    setIsOpen(true)
    const params = new URLSearchParams(searchParams.toString())
    params.delete('assistantConversation')
    const query = params.toString()
    router.replace(query ? `${pathname}?${query}` : pathname)
  }, [orgId, openConversation, pathname, router, searchParams])

  const closePanel = useCallback(() => {
    abort()
    setIsOpen(false)
    setIsExpanded(false)
  }, [abort])

  const toggleExpanded = useCallback(() => {
    setIsExpanded((previous) => !previous)
  }, [])

  const expandedInsets = useMemo(() => {
    const topBarHeight = typeof theme.mixins.toolbar.minHeight === 'number' ? theme.mixins.toolbar.minHeight : 64
    const navWidth = navHidden ? 0 : settings.navCollapsed ? COLLAPSED_NAV_WIDTH : NAV_WIDTH
    return {
      top: bannerOffset + topBarHeight,
      left: navWidth,
      bottom: barHeight
    }
  }, [bannerOffset, barHeight, navHidden, settings.navCollapsed, theme.mixins.toolbar.minHeight])

  useEffect(() => {
    if (!isOpen || !isExpanded) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsExpanded(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isExpanded, isOpen])

  const openPanel = useCallback(() => {
    setIsHistoryOpen(false)
    setIsHelpOpen(false)
    setIsOpen(true)
    setTimeout(() => inputRef.current?.focus(), 60)
  }, [])

  const toggleHistory = useCallback(() => {
    setIsHistoryOpen((previous) => {
      const next = !previous
      if (next) {
        abort()
        setIsOpen(false)
        setIsExpanded(false)
        setIsHelpOpen(false)
      }
      return next
    })
  }, [abort])

  const closeHistory = useCallback(() => setIsHistoryOpen(false), [])

  const toggleHelp = useCallback(() => {
    setIsHelpOpen((previous) => {
      const next = !previous
      if (next) {
        abort()
        setIsOpen(false)
        setIsExpanded(false)
        setIsHistoryOpen(false)
      }
      return next
    })
  }, [abort])

  const closeHelp = useCallback(() => setIsHelpOpen(false), [])

  const hasMessages = messages.length > 0

  return (
    <Root ref={rootRef}>
      {isOpen ? (
        <Panel
          elevation={isExpanded ? 0 : 8}
          $expanded={isExpanded}
          $insetTop={expandedInsets.top}
          $insetLeft={expandedInsets.left}
          $insetBottom={expandedInsets.bottom}
          role="dialog"
          aria-label={t('dialogLabel')}
          {...dropZoneProps}
        >
          {isDragging ? (
            <DropOverlay data-cy="assistant-drop-overlay">
              <UploadFileOutlined color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="subtitle2" color="primary">
                {tAttachments('attach_dropHere')}
              </Typography>
            </DropOverlay>
          ) : null}
          <Header>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesome fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t('headerTitle')}
              </Typography>
              <Chip
                label={t('alpha')}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ height: 20, textTransform: 'uppercase', fontWeight: 600 }}
              />
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={t('notificationsTooltip')}>
                <IconButton
                  size="small"
                  onClick={() => setIsNotificationsOpen((previous) => !previous)}
                  aria-label={t('notificationsLabel')}
                >
                  <BadgeLikeDot visible={opsNotifications.unreadCount > 0}>
                    <NotificationsNone fontSize="small" />
                  </BadgeLikeDot>
                </IconButton>
              </Tooltip>
              {hasMessages && activeConversationId && !conversationReadOnly ? (
                <Tooltip title={t('shareLinkTooltip')}>
                  <IconButton
                    size="small"
                    onClick={() => void copyShareLink()}
                    disabled={isSharingLink}
                    aria-label={t('shareLink')}
                  >
                    {isSharingLink ? <CircularProgress size={16} /> : <LinkOutlined fontSize="small" />}
                  </IconButton>
                </Tooltip>
              ) : null}
              {hasMessages ? (
                <Button size="small" color="inherit" onClick={startNewChat} sx={{ textTransform: 'none', minWidth: 0 }}>
                  {t('newChat')}
                </Button>
              ) : null}
              <Tooltip title={isExpanded ? t('collapseTooltip') : t('expandTooltip')}>
                <IconButton
                  size="small"
                  onClick={toggleExpanded}
                  aria-label={isExpanded ? t('collapseAssistant') : t('expandAssistant')}
                  aria-pressed={isExpanded}
                >
                  {isExpanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('closeTooltip')}>
                <IconButton size="small" onClick={closePanel} aria-label={t('closeAssistant')}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Header>

          {isNotificationsOpen ? (
            <NotificationsOverlay elevation={4}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1 }}>
                <Typography variant="subtitle2">{t('notificationsTitle')}</Typography>
                <IconButton size="small" onClick={() => setIsNotificationsOpen(false)} aria-label={t('closeTooltip')}>
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
              <NotificationsList>
                {opsNotifications.notifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                    {t('notificationsEmpty')}
                  </Typography>
                ) : (
                  opsNotifications.notifications.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      type="button"
                      onClick={() => {
                        void openOpsNotification(notification)
                      }}
                      disabled={
                        !notification.conversationId &&
                        !(
                          notification.entityId &&
                          (notification.entityType === 'firmware_deployment' ||
                            notification.type === 'FIRMWARE_ROLLOUT_COMPLETE' ||
                            notification.type === 'FIRMWARE_ROLLOUT_NEEDS_ATTENTION')
                        )
                      }
                    >
                      <Typography variant="body2" fontWeight={notification.read ? 400 : 600}>
                        {notification.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notification.body}
                      </Typography>
                    </NotificationRow>
                  ))
                )}
              </NotificationsList>
            </NotificationsOverlay>
          ) : null}

          <ConversationArea>
            <MessageScroll>
              {!hasMessages ? (
                <EmptyState>
                  <AutoAwesome sx={{ fontSize: 30, color: 'primary.main', opacity: 0.4 }} />
                  <Typography variant="body2" color="text.secondary" align="center">
                    {t('emptyStateDescription')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" align="center">
                    {t.rich('emptyStateMentionHint', {
                      strong: (chunks) => <strong>{chunks}</strong>
                    })}
                  </Typography>
                  {!conversationReadOnly ? <AssistantEmptySuggestions onSelect={seedInput} /> : null}
                </EmptyState>
              ) : (
                <Stack spacing={1.5}>
                  {conversationReadOnly ? (
                    <ReadOnlyBanner variant="body2" color="text.secondary">
                      {t('sharedReadOnly')}
                    </ReadOnlyBanner>
                  ) : null}
                  {messages.map((message) => (
                    <MessageRow
                      key={message.id}
                      message={message}
                      activityLabels={activityLabels}
                      onSeedInput={conversationReadOnly ? undefined : seedInput}
                      onNavigate={closePanel}
                      disabled={isStreaming}
                      billingComposerEnabled={billingComposerEnabled}
                    />
                  ))}
                  {pendingActionId && pendingActionReason && !conversationReadOnly ? (
                    <ActionCard>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {pendingActionReason}
                      </Typography>
                      {pendingActionPreview ? (
                        <ActionPreview
                          preview={pendingActionPreview}
                          showLabel={t('actionShowDetails')}
                          hideLabel={t('actionHideDetails')}
                        />
                      ) : null}
                      <Button
                        onClick={runProposedAction}
                        size="small"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                      >
                        {t('runAction')}
                      </Button>
                    </ActionCard>
                  ) : null}
                  {actionDispatched && actionDispatchedReason ? (
                    <ActionCard>
                      <Typography variant="body2">{actionDispatchedReason}</Typography>
                    </ActionCard>
                  ) : null}
                  <div ref={messagesEndRef} />
                </Stack>
              )}
            </MessageScroll>

            {isSkillPickerOpen ? (
              <SkillOverlay elevation={8} role="listbox" aria-label={t('skillsPickerLabel')}>
                <SkillOverlayHeader>
                  <SkillOverlayTitle>{t('skillsLabel')}</SkillOverlayTitle>
                  <IconButton size="small" onClick={() => setIsSkillPickerOpen(false)} aria-label={t('closeTooltip')}>
                    <Close fontSize="small" />
                  </IconButton>
                </SkillOverlayHeader>
                <SkillOverlayScroll>
                  {skills.isLoading ? (
                    <SkillPickerEmpty>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        {t('skillsLoading')}
                      </Typography>
                    </SkillPickerEmpty>
                  ) : skills.isError || skills.all.length === 0 ? (
                    <SkillPickerEmpty>
                      <Typography variant="body2" color="text.secondary">
                        {t('skillsEmpty')}
                      </Typography>
                    </SkillPickerEmpty>
                  ) : (
                    <>
                      <SkillPickerListSection
                        skills={skills.builtin}
                        label={t('skillsBuiltin')}
                        onRun={runSkillFromPicker}
                      />
                      <SkillPickerListSection
                        skills={skills.personal}
                        label={t('skillsPersonal')}
                        onRun={runSkillFromPicker}
                      />
                      <SkillPickerListSection
                        skills={skills.organization}
                        label={t('skillsOrganization')}
                        onRun={runSkillFromPicker}
                      />
                    </>
                  )}
                </SkillOverlayScroll>
                <SkillOverlayFooter>
                  <Button
                    component={Link}
                    href={routeDirectory.settings.assistantSkills.root}
                    size="small"
                    fullWidth
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                    onClick={() => setIsSkillPickerOpen(false)}
                  >
                    {t('manageSkillsLink')}
                  </Button>
                </SkillOverlayFooter>
              </SkillOverlay>
            ) : null}
          </ConversationArea>

          <InputArea $dimmed={conversationReadOnly}>
            {conversationReadOnly ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pt: 1 }}>
                {t('sharedReadOnlyHint')}
              </Typography>
            ) : null}
            {mentionCandidates.length > 0 ? (
              <MentionPicker elevation={4} role="listbox">
                {mentionCandidates.map((candidate) => (
                  <MentionItem
                    key={`${candidate.category}-${candidate.id}`}
                    role="option"
                    tabIndex={0}
                    onClick={() => addMentionContext(candidate)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        addMentionContext(candidate)
                      }
                    }}
                  >
                    <MentionIcon>{assistantCategoryIcons[candidate.category]}</MentionIcon>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {candidate.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {candidate.category}
                        {candidate.subLabel ? ` · ${candidate.subLabel}` : ''}
                      </Typography>
                    </Box>
                  </MentionItem>
                ))}
              </MentionPicker>
            ) : null}

            {contextRefs.length > 0 ? (
              <ContextTagStack data-cy="assistant-context-tags">
                {contextRefs.map((contextRef) => (
                  <ComposerContextTag
                    key={`${contextRef.type}-${contextRef.id}`}
                    variant="inline"
                    icon={contextTagIcon(contextRef.type)}
                    label={contextRef.label}
                    onDismiss={conversationReadOnly ? undefined : () => removeContext(contextRef)}
                    dismissLabel={t('removeContext')}
                  />
                ))}
              </ContextTagStack>
            ) : null}

            {attachments.attachments.length > 0 ? (
              <Box sx={{ px: 1.5, pt: 1 }}>
                <PendingAttachmentChips
                  attachments={attachments.attachments}
                  onRemove={attachments.remove}
                  disabled={isStreaming}
                />
              </Box>
            ) : null}

            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              <Tooltip title={actMode ? t('actMode') : t('askMode')}>
                <Chip
                  icon={actMode ? <FastForward sx={{ fontSize: 16 }} /> : <PanToolOutlined sx={{ fontSize: 14 }} />}
                  label={actMode ? t('actModeLabel') : t('askModeLabel')}
                  size="small"
                  color={actMode ? 'warning' : 'default'}
                  variant={actMode ? 'filled' : 'outlined'}
                  onClick={() => setActMode((previous) => !previous)}
                  disabled={conversationReadOnly}
                />
              </Tooltip>
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                placeholder={conversationReadOnly ? t('sharedReadOnlyPlaceholder') : t('placeholder')}
                disabled={conversationReadOnly}
                variant="standard"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    void submit()
                  }
                }}
                onPaste={(event) => {
                  if (event.clipboardData.files.length > 0 && !conversationReadOnly) {
                    event.preventDefault()
                    addFiles(event.clipboardData.files)
                  }
                }}
                InputProps={{ disableUnderline: true }}
              />
            </Stack>

            <ComposerToolbar>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Tooltip title={t('skillsTooltip')}>
                  <ToolbarButton
                    type="button"
                    onClick={() => !conversationReadOnly && setIsSkillPickerOpen((previous) => !previous)}
                    disabled={conversationReadOnly}
                    aria-pressed={isSkillPickerOpen}
                    data-cy="assistant-skills-button"
                  >
                    <Psychology sx={{ fontSize: 16 }} />
                    <span>{t('skillsLabel')}</span>
                    <ExpandMore sx={{ fontSize: 16, opacity: 0.7 }} />
                  </ToolbarButton>
                </Tooltip>
                <Tooltip title={t('focusCurrentPage')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={addCurrentPageContext}
                      disabled={conversationReadOnly}
                      aria-label={t('focusCurrentPage')}
                    >
                      <CropFree fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Stack direction="row" spacing={0.25} alignItems="center">
                <AttachFileButton onFiles={addFiles} disabled={conversationReadOnly || !orgId} size="medium" />
                {isStreaming ? (
                  <Tooltip title={t('stopTooltip')}>
                    <IconButton color="primary" onClick={stopStreaming} aria-label={t('stop')}>
                      <Stop fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('send')}>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => void submit()}
                        disabled={conversationReadOnly || !canSubmit}
                        aria-label={t('send')}
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </ComposerToolbar>
          </InputArea>
        </Panel>
      ) : null}

      {isHistoryOpen ? (
        <HistoryPanel elevation={8} role="dialog" aria-label={t('historyTitle')}>
          <Header>
            <Stack direction="row" spacing={1} alignItems="center">
              <History fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t('historyTitle')}
              </Typography>
            </Stack>
            <Tooltip title={t('closeTooltip')}>
              <IconButton size="small" onClick={closeHistory} aria-label={t('closeHistory')}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Header>
          {isHistoryLoading && historyItems.length === 0 ? (
            <HistoryEmptyState>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary" align="center">
                {t('historyLoading')}
              </Typography>
            </HistoryEmptyState>
          ) : historyError && historyItems.length === 0 ? (
            <HistoryEmptyState>
              <History sx={{ fontSize: 30, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" align="center">
                {t('historyError')}
              </Typography>
              <Button size="small" onClick={() => void refetchHistory()} sx={{ textTransform: 'none' }}>
                {t('historyRetry')}
              </Button>
            </HistoryEmptyState>
          ) : historyItems.length === 0 ? (
            <HistoryEmptyState>
              <History sx={{ fontSize: 30, color: 'primary.main', opacity: 0.4 }} />
              <Typography variant="body2" color="text.secondary" align="center">
                {t('historyEmpty')}
              </Typography>
            </HistoryEmptyState>
          ) : (
            <HistoryList>
              {historyItems.map((item) => (
                <HistoryItem
                  key={item.id}
                  onClick={() => {
                    if (item.id) void openConversation(item.id)
                  }}
                  disabled={loadingConversationId != null}
                  aria-label={item.title?.trim() || t('historyUntitled')}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap fontWeight={500}>
                      {item.title?.trim() || t('historyUntitled')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(item.lastMessageAt ?? item.updatedAt ?? item.createdAt)}
                    </Typography>
                  </Box>
                  {item.id ? (
                    <Tooltip title={t('saveAsSkill')}>
                      <IconButton
                        size="small"
                        aria-label={t('saveAsSkill')}
                        onClick={(event) => {
                          event.stopPropagation()
                          setSaveSkillConversationId(item.id ?? null)
                        }}
                      >
                        <BookmarkAddOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {loadingConversationId === item.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
                  )}
                </HistoryItem>
              ))}
            </HistoryList>
          )}
        </HistoryPanel>
      ) : null}

      {isHelpOpen ? (
        <HelpPanel elevation={8} role="dialog" aria-label={t('helpTitle')}>
          <Header>
            <Stack spacing={0.25}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('helpTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('helpSubtitle')}
              </Typography>
            </Stack>
            <Tooltip title={t('closeTooltip')}>
              <IconButton size="small" onClick={closeHelp} aria-label={t('closeHelp')}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Header>
          <AssistantHelpPanel
            organizationId={orgId}
            pagePath={pathname}
            pageTitle={focusPageRef.label ?? pathname}
            pageContext={focusPageRef.label}
          />
        </HelpPanel>
      ) : null}

      <SaveSkillFromConversationDialog
        open={saveSkillConversationId != null}
        conversationId={saveSkillConversationId}
        onClose={() => setSaveSkillConversationId(null)}
      />

      <Launcher
        variant="text"
        size="small"
        startIcon={<AutoAwesome fontSize="small" />}
        onClick={isOpen ? closePanel : openPanel}
        aria-pressed={isOpen}
      >
        {t('askSynop')}
      </Launcher>
      <Tooltip title={t('historyTooltip')}>
        <BarIconButton
          size="small"
          onClick={toggleHistory}
          aria-pressed={isHistoryOpen}
          aria-label={t('historyTooltip')}
        >
          <History fontSize="small" />
        </BarIconButton>
      </Tooltip>
      <Tooltip title={t('helpTooltip')}>
        <BarIconButton size="small" onClick={toggleHelp} aria-pressed={isHelpOpen} aria-label={t('helpTooltip')}>
          <SupportOutlined fontSize="small" />
        </BarIconButton>
      </Tooltip>
    </Root>
  )
}

function SkillPickerListSection({
  skills,
  label,
  onRun
}: {
  skills: AssistantSkill[]
  label: string
  onRun: (skill: AssistantSkill) => void
}) {
  if (skills.length === 0) return null

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {skills.map((skill) => (
        <SkillPickerItem
          key={skill.id}
          role="option"
          tabIndex={0}
          onClick={() => onRun(skill)}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onRun(skill)
            }
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: '100%' }}>
                {skill.name}
              </Typography>
              {skill.category ? (
                <Chip
                  label={skill.category}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, textTransform: 'capitalize' }}
                />
              ) : null}
            </Stack>
            {skill.description ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mt: 0.25
                }}
              >
                {skill.description}
              </Typography>
            ) : null}
          </Box>
          <PlayArrowOutlined fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
        </SkillPickerItem>
      ))}
    </Box>
  )
}

type MessageRowProps = {
  message: AssistantMessage
  activityLabels: AssistantActivityLabels
  /** Seeds the input with a request; absent on read-only conversations. */
  onSeedInput?: (text: string) => void
  /** Closes the bar once a draft card navigates to a wizard. */
  onNavigate: () => void
  disabled: boolean
  /** When false, hide billing draft cards even if the stream carried them. */
  billingComposerEnabled: boolean
}

function MessageRow({
  message,
  activityLabels,
  onSeedInput,
  onNavigate,
  disabled,
  billingComposerEnabled
}: MessageRowProps) {
  const { text, skills } = useMemo(() => parseSkillInvocations(message.content), [message.content])

  if (message.role === 'user') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <UserBubble>{message.content}</UserBubble>
        {message.attachments ? <MessageAttachmentChips attachments={message.attachments} /> : null}
      </Box>
    )
  }

  const activityItems: AssistantActivityItem[] = [
    ...(message.toolCalls?.map((toolCall) => ({
      id: toolCall.id,
      label: toolCall.summary || toolCall.name,
      detail: toolCall.summary ? toolCall.name : undefined,
      kind: 'tool' as const,
      status: toolCall.status
    })) ?? []),
    ...skills.map((skill) => ({
      id: skill.id,
      label: skill.name,
      detail: skill.params,
      kind: 'skill' as const,
      status: 'done' as const
    }))
  ]
  const reasoning = message.thinking?.trim() ?? ''
  const phaseMessage = message.phaseMessage?.trim() ?? ''
  const isActivityActive = Boolean(message.streaming || activityItems.some((item) => item.status === 'running'))

  return (
    <Stack spacing={1}>
      <AssistantMessageStack
        activityFirst={!isActivityActive}
        content={
          text ? (
            <AssistantBubble sx={{ minWidth: 0 }}>
              <AssistantMarkdown>{text}</AssistantMarkdown>
            </AssistantBubble>
          ) : null
        }
        activity={
          <AssistantActivity
            active={isActivityActive}
            items={activityItems}
            labels={activityLabels}
            phaseMessage={phaseMessage}
            reasoning={reasoning}
          />
        }
      />
      {billingComposerEnabled && message.billingDrafts ? (
        <BillingDraftMessageBlock
          drafts={message.billingDrafts}
          onAskApply={onSeedInput}
          onNavigate={onNavigate}
          disabled={disabled}
        />
      ) : null}
    </Stack>
  )
}

type ActionPreviewProps = {
  preview: string
  showLabel: string
  hideLabel: string
}

/** Collapsible request body / diff under an approval card, so a long billing preview does not push the button away. */
function ActionPreview({ preview, showLabel, hideLabel }: ActionPreviewProps) {
  const [open, setOpen] = useState(false)
  return (
    <Box sx={{ width: '100%' }}>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        sx={{ textTransform: 'none', px: 0.5, minWidth: 0 }}
      >
        {open ? hideLabel : showLabel}
      </Button>
      {open ? <ActionPreviewBody data-cy="assistant-action-preview">{preview}</ActionPreviewBody> : null}
    </Box>
  )
}

type ParsedSkillInvocation = { id: string; name: string; params?: string }

// The model sometimes emits pseudo tool-call XML (e.g. <use_skill><skill_name>x</skill_name>...) as plain text
// instead of using a real tool. Pull those blocks out so we can render them as a chip (like operations) rather
// than dumping raw XML into the Markdown bubble.
function parseSkillInvocations(content: string): { text: string; skills: ParsedSkillInvocation[] } {
  if (!content.includes('<use_skill')) {
    return { text: content, skills: [] }
  }

  const skills: ParsedSkillInvocation[] = []
  let text = content.replace(/<use_skill\b[^>]*>([\s\S]*?)<\/use_skill>/gi, (_match, inner: string) => {
    const name = /<skill_name>([\s\S]*?)<\/skill_name>/i.exec(inner)?.[1]?.trim()
    const params = /<parameters>([\s\S]*?)<\/parameters>/i.exec(inner)?.[1]?.trim()
    skills.push({ id: `skill-${skills.length}`, name: name || 'skill', params: params || undefined })
    return ''
  })

  // A block may still be streaming and not yet closed — hide everything from the open tag onward.
  const openIndex = text.indexOf('<use_skill')
  if (openIndex >= 0) {
    text = text.slice(0, openIndex)
  }

  return { text: text.trim(), skills }
}

function applyActionStreamEvent(
  event: AssistantStreamEvent,
  messageId: string,
  setMessages: Dispatch<SetStateAction<AssistantMessage[]>>
) {
  const updateProgressMessage = (updater: (message: AssistantMessage) => AssistantMessage) => {
    setMessages((previous) => previous.map((message) => (message.id === messageId ? updater(message) : message)))
  }

  if (event.type === 'phase') {
    updateProgressMessage((message) => ({
      ...message,
      phase: event.phase,
      phaseMessage: event.message
    }))
    return
  }

  if (event.type === 'delta') {
    updateProgressMessage((message) => ({ ...message, content: `${message.content}${event.text}` }))
    return
  }

  if (event.type === 'thinking_delta') {
    updateProgressMessage((message) => ({
      ...message,
      thinking: `${message.thinking ?? ''}${event.text}`
    }))
    return
  }

  if (event.type === 'tool_call') {
    updateProgressMessage((message) => ({
      ...message,
      toolCalls: [
        ...(message.toolCalls ?? []),
        { id: crypto.randomUUID(), name: event.name, status: 'running' as const }
      ]
    }))
    return
  }

  if (event.type === 'tool_result') {
    updateProgressMessage((message) => {
      const toolCalls = message.toolCalls ?? []
      let matched = false
      const next = [...toolCalls]
        .reverse()
        .map((toolCall) => {
          if (!matched && toolCall.status === 'running' && toolCall.name === event.name) {
            matched = true
            return { ...toolCall, status: 'done' as const, summary: event.summary }
          }
          return toolCall
        })
        .reverse()
      return { ...message, toolCalls: next }
    })
    return
  }

  if (event.type === 'artifact') {
    updateProgressMessage((message) => ({
      ...message,
      content: appendArtifactMarkdown(message.content, event)
    }))
    return
  }

  if (event.type === 'action_complete') {
    const resultText =
      event.status === 'done' ? (event.summary ?? '') : (event.error ?? 'The diagnostics workflow failed.')
    updateProgressMessage((message) => {
      let finalContent = message.content
      if (resultText) {
        finalContent = message.content ? `${resultText}\n\n${message.content}` : resultText
      }
      return {
        ...message,
        streaming: false,
        phase: event.status,
        phaseMessage: undefined,
        content: finalContent,
        toolCalls: message.toolCalls?.map((toolCall) =>
          toolCall.status === 'running' ? { ...toolCall, status: 'done' as const } : toolCall
        )
      }
    })
  }
}

function applyStreamEvent(
  event: AssistantStreamEvent,
  setMessages: Dispatch<SetStateAction<AssistantMessage[]>>,
  setPendingActionId: Dispatch<SetStateAction<string | null>>,
  setPendingActionReason: Dispatch<SetStateAction<string | null>>,
  setPendingActionType: Dispatch<SetStateAction<string | null>>,
  setPendingActionLongRunning: Dispatch<SetStateAction<boolean>>,
  setPendingActionPreview: Dispatch<SetStateAction<string | null>>
) {
  if (event.type === 'delta') {
    updateLastAssistant(setMessages, (message) => ({ ...message, content: `${message.content}${event.text}` }))
    return
  }

  if (event.type === 'thinking_delta') {
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      thinking: `${message.thinking ?? ''}${event.text}`
    }))
    return
  }

  if (event.type === 'tool_call') {
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      toolCalls: [
        ...(message.toolCalls ?? []),
        { id: crypto.randomUUID(), name: event.name, status: 'running' as const }
      ]
    }))
    return
  }

  if (event.type === 'tool_result') {
    updateLastAssistant(setMessages, (message) => {
      const toolCalls = message.toolCalls ?? []
      let matched = false
      const next = [...toolCalls]
        .reverse()
        .map((toolCall) => {
          if (!matched && toolCall.status === 'running' && toolCall.name === event.name) {
            matched = true
            return { ...toolCall, status: 'done' as const, summary: event.summary }
          }
          return toolCall
        })
        .reverse()
      return { ...message, toolCalls: next }
    })
    return
  }

  if (event.type === 'propose_action') {
    setPendingActionId(event.actionId)
    setPendingActionReason(event.reason ?? 'The assistant proposed an action.')
    setPendingActionType(event.actionType ?? null)
    setPendingActionLongRunning(Boolean(event.longRunning))
    setPendingActionPreview(event.preview ?? null)
    return
  }

  // Billing proposals (contract §8) render as rich cards under the turn that produced them.
  if (event.type === 'billing_draft') {
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      billingDrafts: upsertBillingDraft(message.billingDrafts ?? [], event.draft)
    }))
    return
  }

  if (event.type === 'artifact') {
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      content: appendArtifactMarkdown(message.content, event)
    }))
    return
  }

  // The structured clarification card lives on the generative dashboard page; in the
  // chat bar we render the questions as plain text so the user can answer by typing.
  if (event.type === 'clarification') {
    const questionLines = event.questions.map((question) => {
      const options = question.options?.length ? ` (${question.options.map((option) => option.label).join(' / ')})` : ''
      return `- ${question.prompt}${options}`
    })
    const text = [event.intro || 'I need a bit more detail before I continue:', ...questionLines].join('\n')
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      content: message.content ? `${message.content}\n\n${text}` : text
    }))
    return
  }

  // Panels render on the generative dashboard page; in the chat bar show the summary text.
  if (event.type === 'panel') {
    const title = event.panel.title || event.panel.panelType
    const note = event.panel.summaryText ? `**${title}** — ${event.panel.summaryText}` : `**${title}**`
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      content: message.content ? `${message.content}\n\n${note}` : note
    }))
    return
  }

  if (event.type === 'done' || event.type === 'cancelled') {
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      streaming: false,
      toolCalls: message.toolCalls?.map((toolCall) =>
        toolCall.status === 'running' ? { ...toolCall, status: 'done' as const } : toolCall
      )
    }))
    return
  }

  if (event.type === 'error') {
    updateLastAssistant(setMessages, (message) => ({
      ...message,
      streaming: false,
      content: message.content || event.message
    }))
  }
}

/** A re-submitted draft (same draftId) replaces the earlier version in place; new drafts append. */
function upsertBillingDraft(drafts: BillingDraft[], draft: BillingDraft): BillingDraft[] {
  const index = drafts.findIndex((existing) => existing.draftId === draft.draftId)
  if (index < 0) return [...drafts, draft]
  const next = [...drafts]
  next[index] = draft
  return next
}

function updateLastAssistant(
  setMessages: Dispatch<SetStateAction<AssistantMessage[]>>,
  updater: (message: AssistantMessage) => AssistantMessage
) {
  setMessages((previous) => {
    if (previous.length === 0) return previous
    const lastMessage = previous.at(-1)
    if (!lastMessage || lastMessage.role !== 'assistant') return previous
    const copy = [...previous]
    copy[copy.length - 1] = updater(lastMessage)
    return copy
  })
}

function appendArtifactMarkdown(content: string, event: Extract<AssistantStreamEvent, { type: 'artifact' }>): string {
  const label = escapeMarkdownLabel(event.filename || 'artifact')
  if (!event.url) {
    const fallbackLine = `${label} was generated, but no download link is available.`
    return content ? `${content}\n\n${fallbackLine}` : fallbackLine
  }
  const line = event.inline ? `![${label}](<${event.url}>)` : `[Download ${label}](<${event.url}>)`
  return content ? `${content}\n\n${line}` : line
}

function escapeMarkdownLabel(value: string): string {
  return value.replace(/[[\]\\]/g, '\\$&')
}

const Root = styled(Box)(({ theme }) => ({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: theme.zIndex.drawer + 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.5),
  minHeight: 40,
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(0.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper
}))

const Launcher = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(0.75),
  paddingInline: theme.spacing(1.25),
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '& .MuiButton-startIcon': {
    color: theme.palette.primary.main
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary
  },
  '&[aria-pressed="true"]': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary
  }
}))

const BarIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&[aria-pressed="true"]': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected
  }
}))

const HistoryPanel = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  right: theme.spacing(2),
  width: 'min(360px, calc(100vw - 32px))',
  maxHeight: 'min(480px, calc(100vh - 120px))',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`
}))

const HistoryEmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(4, 3),
  minHeight: 160
}))

const HistoryList = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5)
}))

const HistoryItem = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  textAlign: 'left',
  padding: theme.spacing(1, 1.25),
  borderRadius: theme.spacing(1),
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&.Mui-disabled': { opacity: 0.6 }
}))

const HelpPanel = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  right: theme.spacing(2),
  width: 'min(380px, calc(100vw - 32px))',
  maxHeight: 'min(640px, calc(100vh - 120px))',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`
}))

type PanelProps = {
  $expanded?: boolean
  $insetTop?: number
  $insetLeft?: number
  $insetBottom?: number
}

const Panel = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== '$expanded' && prop !== '$insetTop' && prop !== '$insetLeft' && prop !== '$insetBottom'
})<PanelProps>(({ theme, $expanded, $insetTop = 0, $insetLeft = 0, $insetBottom = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  ...($expanded
    ? {
        position: 'fixed',
        top: $insetTop,
        left: $insetLeft,
        right: 0,
        bottom: $insetBottom,
        width: 'auto',
        maxHeight: 'none',
        borderRadius: 0,
        borderBottom: 'none',
        zIndex: theme.zIndex.drawer,
        backgroundColor: theme.palette.background.paper
      }
    : {
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        right: theme.spacing(2),
        width: 'min(400px, calc(100vw - 32px))',
        maxHeight: 'min(640px, calc(100vh - 120px))',
        borderRadius: theme.spacing(2)
      })
}))

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1, 1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0
}))

const ConversationArea = styled(Box)({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column'
})

const MessageScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 160,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: theme.spacing(2)
}))

const EmptyState = styled(Box)(({ theme }) => ({
  height: '100%',
  minHeight: 180,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2)
}))

const UserBubble = styled(Box)(({ theme }) => ({
  maxWidth: '85%',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(2),
  borderBottomRightRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: theme.typography.body2.fontSize,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
}))

const AssistantBubble = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(2),
  borderBottomLeftRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  wordBreak: 'break-word'
}))

const ActionCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
  backgroundColor: alpha(theme.palette.warning.main, 0.08)
}))

const ActionPreviewBody = styled('pre')(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(1),
  maxHeight: 220,
  overflow: 'auto',
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.text.primary, 0.05)
}))

const ReadOnlyBanner = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.info.main, 0.08),
  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
}))

const DropOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 3,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  pointerEvents: 'none',
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  border: `2px dashed ${theme.palette.primary.main}`
}))

const InputArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$dimmed'
})<{ $dimmed?: boolean }>(({ theme, $dimmed }) => ({
  position: 'relative',
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  opacity: $dimmed ? 0.92 : 1
}))

const ContextTagStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.common.black, 0.02)
}))

const ComposerToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.25, 1)
}))

const ToolbarButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  margin: 0,
  padding: theme.spacing(0.5, 0.75),
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  background: 'transparent',
  color: theme.palette.text.secondary,
  font: 'inherit',
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover:not(:disabled)': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'default'
  },
  '&[aria-pressed="true"]': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary
  }
}))

const MentionPicker = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  left: theme.spacing(1.5),
  right: theme.spacing(1.5),
  maxHeight: 220,
  overflowY: 'auto',
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`
}))

const SkillOverlay = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: theme.spacing(1.5),
  borderTopRightRadius: theme.spacing(1.5)
}))

const SkillOverlayHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5, 0.5, 0.5, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0
}))

const SkillOverlayTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary
}))

const SkillOverlayScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: theme.spacing(0.5)
}))

const SkillOverlayFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(0.5),
  flexShrink: 0
}))

const SkillPickerEmpty = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2)
}))

const SkillPickerItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.action.hover }
}))

const MentionItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': { backgroundColor: theme.palette.action.hover }
}))

const MentionIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '& svg': { fontSize: 18 }
}))

const BadgeLikeDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: boolean }>(({ theme, visible }) => ({
  position: 'relative',
  display: 'inline-flex',
  '&::after': visible
    ? {
        content: '""',
        position: 'absolute',
        top: 2,
        right: 2,
        width: 7,
        height: 7,
        borderRadius: '50%',
        backgroundColor: theme.palette.error.main,
        border: `1px solid ${theme.palette.background.paper}`
      }
    : undefined
}))

const NotificationsOverlay = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: 48,
  right: theme.spacing(1),
  width: 320,
  maxHeight: 280,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column'
}))

const NotificationsList = styled(Box)({
  overflowY: 'auto',
  flex: 1
})

const NotificationRow = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.25),
  width: '100%',
  textAlign: 'left',
  padding: theme.spacing(1, 1.5),
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&.Mui-disabled': {
    opacity: 0.6
  }
}))

function contextTagIcon(type: string) {
  if (type in assistantCategoryIcons) {
    return assistantCategoryIcons[type as keyof typeof assistantCategoryIcons]
  }
  return <CropFree fontSize="small" />
}
